import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ImageOff, ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { QuotationCTA } from "@/components/site/Shared";
import { useSEO } from "@/hooks/useSEO";
import { PROJECT_CATEGORIES, IMAGES } from "@/data/catalog";
import { api } from "@/lib/api";

export default function Projects() {
  useSEO({
    title: "Projects Portfolio — Renovation & Metal Works Singapore",
    description: "Explore WAAN METAL's portfolio of residential, commercial, industrial, metal works and interior design projects in Singapore.",
  });
  const [filter, setFilter] = useState("All");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/projects", { params: filter === "All" ? {} : { category: filter } })
      .then(({ data }) => setProjects(data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <Layout>
      <section className="relative bg-[#0A1128] py-20 overflow-hidden">
        <div className="absolute inset-0"><img src={IMAGES.industrial1} alt="" className="w-full h-full object-cover opacity-20" /><div className="absolute inset-0 bg-[#0A1128]/88" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-3">Our Work</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Projects portfolio</h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg">Verified completed work across residential, commercial, industrial, metal works and interior design.</p>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="project-filters">
            {PROJECT_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`project-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  filter === c ? "bg-[#0A1128] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[1,2,3].map((i) => <div key={i} className="h-72 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
          ) : projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {projects.map((p) => (
                <div key={p.id} className="group rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow" data-testid={`project-card-${p.id}`}>
                  <div className="h-52 overflow-hidden bg-slate-200 relative">
                    {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="flex items-center justify-center h-full text-slate-400"><ImageOff size={28} /></div>}
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#B87333] text-[#0A1128] text-xs font-bold">{p.category}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-slate-900">{p.title}</h3>
                    {p.description && <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{p.description}</p>}
                    <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                      {p.location && <span className="flex items-center gap-1"><MapPin size={13} /> {p.location}</span>}
                      {p.completion_year && <span className="flex items-center gap-1"><Calendar size={13} /> {p.completion_year}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-14 text-center" data-testid="projects-empty">
              <h3 className="text-xl font-semibold text-slate-900">Portfolio being updated</h3>
              <p className="mt-2 text-slate-600 max-w-md mx-auto">We only publish verified, completed projects. Our portfolio for this category is being updated — contact us for references relevant to your project.</p>
              <Link to="/request-quotation" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold">Request a Quotation <ArrowRight size={16} /></Link>
            </div>
          )}
        </div>
      </section>

      <QuotationCTA />
    </Layout>
  );
}
